import type { AdPlacement, AdProvider, AdShowResult } from './types';

export interface MockAdProviderOptions {
  autoResolve?: AdShowResult;
  autoResolveDelayMs?: number;
}

export class MockAdProvider implements AdProvider {
  readonly kind = 'mock';

  constructor(private readonly options: MockAdProviderOptions = {}) {}

  show(placement: AdPlacement): Promise<AdShowResult> {
    if (this.options.autoResolve !== undefined) {
      return new Promise((resolve) => {
        setTimeout(resolve, this.options.autoResolveDelayMs ?? 0, this.options.autoResolve);
      });
    }

    return this.showOverlay(placement);
  }

  /**
   * 見た目は「実広告が入る枠」のプレースホルダーに徹する。実配信では広告 SDK が
   * 提供する枠・カウントダウン・閉じるボタンをそのまま出すため（ADR-0005）、
   * ここで広告らしい体裁を作り込まない。破線の枠と枠サイズの表記だけを置き、
   * 結果を選ぶボタン列は開発用と分かる見せ方にしている。
   * スタイルは styles.css の .mock-ad* が持つ。
   */
  private showOverlay(placement: AdPlacement): Promise<AdShowResult> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'mock-ad';

      const frame = document.createElement('div');
      frame.className = 'mock-ad__frame';

      const eyebrow = document.createElement('p');
      eyebrow.className = 'mock-ad__eyebrow';
      eyebrow.textContent = 'AD PLACEHOLDER';

      const title = document.createElement('p');
      title.className = 'mock-ad__title';
      title.textContent = 'ここに実広告が表示されます';

      const meta = document.createElement('p');
      meta.className = 'mock-ad__meta';
      meta.textContent = `${placement.id} · ${placement.format} · ${placement.weight}`;

      const progress = document.createElement('div');
      progress.className = 'mock-ad__progress';
      const progressFill = document.createElement('div');
      progressFill.className = 'mock-ad__progress-fill';
      progress.append(progressFill);

      const choicesLabel = document.createElement('p');
      choicesLabel.className = 'mock-ad__choices-label';
      choicesLabel.textContent = 'モック実装：返す結果を選びます';

      const buttons = document.createElement('div');
      buttons.className = 'mock-ad__choices';
      const choices: readonly [string, AdShowResult][] = [
        ['視聴完了', 'completed'],
        ['広告なし (noAd 1)', 'unavailable'],
        ['表示エラー (noAd 2)', 'failed'],
        ['閉じる', 'cancelled'],
      ];
      const resultButtons: HTMLButtonElement[] = [];

      for (const [label, result] of choices) {
        const button = document.createElement('button');
        resultButtons.push(button);
        button.type = 'button';
        button.textContent = label;
        button.className = result === 'completed'
          ? 'mock-ad__choice mock-ad__choice--primary'
          : 'mock-ad__choice';
        button.addEventListener('click', () => {
          overlay.remove();
          resolve(result);
        }, { once: true });
        buttons.append(button);
      }

      overlay.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') {
          return;
        }
        const first = resultButtons[0];
        const last = resultButtons.at(-1);
        if (first === undefined || last === undefined) {
          event.preventDefault();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      frame.append(eyebrow, title, meta, progress);
      overlay.append(frame, choicesLabel, buttons);
      document.body.append(overlay);
      resultButtons[0]?.focus();
    });
  }
}
